<template>
  <div>
    <select-field ref="PaymentMeansSummary"
                  :value=display
                  :required=required
                  :editFieldInfo=editFieldInfo
                  :items=items
                  :disabled=readOnly
                  :canEdit=canEdit
                  :selectText=selectText
                  :hideLabel='hideLabel'
                  :dataTable='dataTable'
                  :showMissing=showMissing
                  :notOrdered=notOrdered
                  :fiche=fiche
                  @updated=setUpdated
                  @onEdit=onEdit
                  @error=setError>
    </select-field> 

    <section-container-panel v-if="dialogOpen"
                             :section="section"
                             :dialogOpen="dialogOpen"
                             :extended="true"
                             :helper="helper"
                             :ressourceHeader="ressourceHeader"
                             :fiche="fiche"
                             @updated="setSectionUpdated"
                             @closeDialog="closeDialog"
                             @saveOrCancel="setSaveOrCancel"
                             @status="statusUpdated">
    </section-container-panel>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from "vue";
  import Section from '@/framework/base/classes/Section';
  import { FieldInfo, EditFieldType, FieldStatus } from '@/framework/base/components/FieldInfo'
  import SummaryBase from '@/framework/base/components/SummaryBase';
  import { Sections } from '@/generated/classes/AppSchema';
  import SelectField from '@/framework/base/components/SelectField.vue'
  import Item from '@/framework/base/classes/Item';
  import PaymentMeans from '@/extend/classes/PaymentMeans';
  import Owner from '@/extend/classes/Owner';
  import SectionContainerPanel from '@/framework/base/components/SectionContainerPanel.vue';
  import SectionPayment from '@/custom/sections/SectionPayment.vue';
  import { storePaymentMeans } from '@/generated/stores';
  import ListData, { ListFilter } from '@/extend/classes/ListData';

  export default defineComponent({
    extends: SummaryBase,
    components: {
      SelectField,
      SectionContainerPanel,
    },

    props: {
      helper: {
        type: Boolean
      }
    },

    beforeUnmount (): void {
      this.clearStore();
    },

    data() {
      const listPaymentMeans: PaymentMeans[] = [];
      const tmpPaymentMeans: PaymentMeans = new PaymentMeans();
      return {
        selectedPaymentMeansId: 0,
        selectedPaymentMeans: new PaymentMeans(),
        tmpPaymentMeans,
        newContext: true,
        reset: true,
        listUpdated: false,
        listPaymentMeans
      }
    },

    computed: {
      editFieldInfo (): FieldInfo {
        const type = EditFieldType.SUMMARY;
        const precision = 2;
        const format = null;
        const mask = null;
        const focus = null;
        const live = null;
        return new FieldInfo(this.ressource, type, precision, mask, format, focus, live, this.theme);
      },

      section (): Section {
        const section = new Section();
        section.component = SectionPayment;
        section.type = 'paymentMeans';
        section.data = this.selectedPaymentMeans;
        section.picto = 'SectionPaymentPicto';
        section.contextId = this.contextId;
        section.showButton = true;
        section.autoSave = true;
        if (this.sectionName) {
          section.sectionName = this.sectionName;
        }
        if (this.requiredAttributs) {
          section.requiredAttributs = this.requiredAttributs;
        }
        return section;
      },

      listItems (): PaymentMeans[] {
        if (this.reset != this.toggleReset) {
          this.loadItems();
        }
        return this.listPaymentMeans;
      },

      items (): Item[] {
        const items: Item[] = [];
        items.push(new Item(0, this.newLabel != undefined && this.newLabel != '' ? this.newLabel : "Nouveau compte bancaire")); 
        const paymentMeanss = this.getItems(this.listItems, this.sectionName);
        paymentMeanss.forEach(paymentMeans => {
          if (paymentMeans && paymentMeans.id > 0 ) {
            items.push(new Item(paymentMeans.id, this.getDisplay(paymentMeans)));
          }
        })
        return items;
      },

      display (): string {
        const value = this.value;
        let item = this.items.find(a => a.key == value)
        this.changeContext();
        if (this.items.length == 2 && this.newContext && this.required && this.autoSelect) {
          this.changeContext();
          item = this.items.find(a => a.key == this.items[1].key);
          if (item && item.key != this.initValue) {
            this.itemChanged(item.key);
            this.setStatusUpdated(true);
            if (this.initValue != 0 || this.items.length != 2) {
              this.statusUpdated(this.status);
            }
          }
        }
        if (item) {
          if (this.resetToggle()) {
            this.setStatusUpdated(true);
            this.statusUpdated(this.status);
          }
          const itemDefined = item;
          const paymentMeans = this.listPaymentMeans.find(a => a.id == itemDefined.key);
          if (paymentMeans && paymentMeans.id != this.selectedPaymentMeansId) {
            this.setSelectedPaymentMeans(paymentMeans);
          }
          if (item.key == 0 && this.selectedPaymentMeansId == 0) {
            this.setSelectedPaymentMeans(this.newPaymentMeans());
          }
          this.setSelectedPaymentMeansId(paymentMeans ? paymentMeans.id : this.selectedPaymentMeans.id);
        }
        const res = item && item.key != 0 ? item.title : '';
        if (this.cancelChanges && item) {
          this.itemChanged(item.key);
        }
        this.setAttribut(this.selectedPaymentMeans, this.owner, this.parentObj)
        return res
      },

      canEdit (): string {
        return true
      }
    },

    methods: {
      setSelectedPaymentMeans(value: PaymentMeans) {
        this.selectedPaymentMeans = value;
      },
      setSelectedPaymentMeansId(id: number) {
        this.selectedPaymentMeansId = id;
      },
      setNewContext(value: boolean) {
        this.newContext = value;
      },
      setStatusUpdated(value: boolean) {
        this.status.updated = value;
      },
      newPaymentMeans (): PaymentMeans {
        this.clearStore();
        const newPaymentMeans = storePaymentMeans.createNewPaymentMeans();
        this.tmpPaymentMeans = newPaymentMeans;
        return newPaymentMeans;
      },

      loadItems (): Promise<boolean> {
        this.reset = this.toggleReset;
        return new Promise((resolve) => {
          if (this.filter) {
            PaymentMeans.list(this.filter)
              .then((data: ListData) => {
                const items = data.reccords as PaymentMeans[];
                this.listPaymentMeans = [];
                items.forEach((item) => {
                  this.listPaymentMeans.push(PaymentMeans.fromJson(item));
                })
                this.listUpdated = true;
                resolve(true)
              }).catch(() => {
                //Catch empty list
              });
          } else {
            resolve(false);
          }
        })
      },

      clearStore() : void {
        if (this.tmpPaymentMeans != null && this.tmpPaymentMeans.id < 0) {
          storePaymentMeans.mPaymentMeansDeleted(this.tmpPaymentMeans.id);
        }
      },

      contextChanged() : void {
        this.newContext = true;
        this.selectedPaymentMeansId = 0;
        this.selectedPaymentMeans = new PaymentMeans();
        this.initValue = this.value;
      },

      setSaveOrCancel () {
        this.cancelChanges = true;
      },

      // eslint-disable-next-line
      getItems(paymentMeans: PaymentMeans[], section: String): PaymentMeans[] {
        const items: PaymentMeans[] = [];
        paymentMeans.forEach((item) => {
          items.push(PaymentMeans.fromJson(item));
        })
        return items;
      },

        // eslint-disable-next-line
      getDisplay(paymentMeans: PaymentMeans): string {
        return '';
      },

      // eslint-disable-next-line
      setAttribut(paymentMeans: PaymentMeans, owner: Owner, parentObj: unknown): void {
        // to implement
      },

      setSectionUpdated(paymentMeans: PaymentMeans) {
        const item = this.items.find(a => a.key == paymentMeans.id);
        if (!item && paymentMeans.id > 0) {
          this.loadItems().then(() => {
            const item = this.items.find(a => a.key == paymentMeans.id);
            if (item) {
              this.setUpdated(item);
            }
          });
        }
        if (item) {
          this.setUpdated(item);
        }
      },

      setUpdated (item: Item): void {
        if (item.key == 0) {
          this.dialogOpen = true;
          this.selectedPaymentMeans = this.newPaymentMeans();
        }
        this.itemChanged(item.key);
        this.status.updated = this.initValue != item.key;
        this.status.error = false;
        this.statusUpdated(this.status);
      },

      setRestored (paymentMeans: PaymentMeans) {
        this.selectedPaymentMeans = paymentMeans;
      },

      itemChanged (id: number): void {
        this.$emit('update:value', id);
      },

      statusUpdated (status: FieldStatus): void {
        this.$emit('status', status);
      },

      missing (): boolean {
        // @ts-expect-error : generated method
        return this.$refs.PaymentMeansSummary.missing();
      },

      touch (): boolean {
        // @ts-expect-error : generated method
        return this.$refs.PaymentMeansSummary.touch();
      }
    },
  })
</script>
